// Game status constants
export const GAME_STATES = {
  SCHEDULED: 'Scheduled',
  POSTPONED: 'Postponed',
  IN_PROGRESS: 'In Progress',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  DELAYED: 'Delayed',
  SUSPENDED: 'Suspended',
  FINAL: 'Final',
  NOT_YET_SCHEDULED: 'Not Yet Scheduled',
} as const;

// Game statuses that indicate issues
export const ISSUE_STATUSES = [
  GAME_STATES.CANCELLED,
  GAME_STATES.POSTPONED,
  GAME_STATES.DELAYED,
  GAME_STATES.SUSPENDED,
];

export type GameStateType = typeof GAME_STATES[keyof typeof GAME_STATES];
export type IssueStatusType = typeof ISSUE_STATUSES[keyof typeof ISSUE_STATUSES];

export interface GameStateInfo {
  isPreGame: boolean;
  isPostponed: boolean;
  isLiveGame: boolean;
  isPostGame: boolean;
  isCancelled: boolean;
  isDelayed: boolean;
  isSuspended: boolean;
  isFinal: boolean;
  isAnnounced: boolean;
}

export function getGameStateInfo(gameState: string): GameStateInfo {
  return {
    isPreGame: gameState === GAME_STATES.SCHEDULED,
    isPostponed: gameState === GAME_STATES.POSTPONED,
    isLiveGame: gameState === GAME_STATES.IN_PROGRESS,
    isPostGame: gameState === GAME_STATES.COMPLETED,
    isCancelled: gameState === GAME_STATES.CANCELLED,
    isDelayed: gameState === GAME_STATES.DELAYED,
    isSuspended: gameState === GAME_STATES.SUSPENDED,
    isFinal: gameState === GAME_STATES.FINAL,
    isAnnounced: gameState === GAME_STATES.NOT_YET_SCHEDULED,
  };
}

/**
 * Fetches the current game status from the consolidated API
 * 
 * @param game The game object containing necessary identifiers
 * @returns The current status of the game
 */
export const fetchGameStatus = async (
  game: { 
    gameId: number | string; 
    gameNumber?: number | string;
    sportCodeLk: string;
    seasonId: number | string;
  }
): Promise<string> => {
  try {

    // Build the API endpoint URL with required parameters
    let apiEndpoint = `/api/live-game-data?gameId=${game.gameId}&sport=${game.sportCodeLk}&seasonId=${game.seasonId}&gameNumber=${game.gameNumber}&_t=${Date.now()}`;

    const response = await fetch(apiEndpoint, { 
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });
    
    if (!response.ok) throw new Error('Failed to fetch game status');
    

    // For consolidated API, extract the status from the gameStatus object
    const data = await response.json();
    
    return data.gameStatus;

  } catch (error) {
    console.error('Error fetching game status:', error);
    // Return scheduled as fallback
    return GAME_STATES.SCHEDULED;
  }
};

/**
 * Calls the webhook endpoint when a game status changes from IN_PROGRESS to COMPLETED
 * @param gameId The ID of the game
 * @param previousStatus The previous status of the game
 * @param currentStatus The current status of the game
 */
export const notifyGameStatusChange = async (
  gameId: string | number,
  previousStatus: string,
  currentStatus: string
): Promise<void> => {
  // Only notify for relevant status changes to avoid unnecessary API calls
  if (previousStatus === GAME_STATES.IN_PROGRESS && 
      currentStatus === GAME_STATES.COMPLETED) {
    
    try {
      // Get the webhook secret from environment
      const webhookSecret = process.env.NEXT_PUBLIC_WEBHOOK_SECRET;
      
      // Call the webhook endpoint
      const response = await fetch('/api/webhook', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${webhookSecret}`,
        },
        body: JSON.stringify({
          gameId,
          previousStatus,
          currentStatus,
          timestamp: new Date().toISOString(),
        }),
      });
      
      if (!response.ok) {
        console.error('Failed to notify webhook:', await response.text());
      }
    } catch (error) {
      console.error('Error notifying webhook:', error);
    }
  }
}; 